import { ApiError } from "./api-error";
export { ApiError } from "./api-error";
import type { ZodType } from "zod";
import { env } from "./env";
import { acquireAppCheckToken, getFirebaseAuth } from "./firebase";
import {
  actionResponseSchema,
  addressSchema,
  adminDashboardSchema,
  adminPlanInputSchema,
  adminPlanSchema,
  adminPlansResponseSchema,
  adminSettingsInputSchema,
  adminSettingsSchema,
  adminSubscriptionSchema,
  adminSubscriptionsResponseSchema,
  adminOfferSchema,
  adminOfferInputSchema,
  adminOfferRepriceInputSchema,
  adminOfferRepriceResponseSchema,
  adminOfferStateInputSchema,
  adminOfferSyncResponseSchema,
  adminOfferUpsertResponseSchema,
  adminProductCatalogSchema,
  auditResponseSchema,
  brandingSchema,
  brandingSyncResultSchema,
  checkoutResponseSchema,
  checkoutViewSchema,
  createIyzicoPricingPlanInputSchema,
  createIyzicoProductInputSchema,
  customersResponseSchema,
  exchangeResponseSchema,
  iyzicoCatalogPricingPlanSchema,
  iyzicoCatalogProductSchema,
  legalDocumentSchema,
  orderSyncIssuesResponseSchema,
  paymentResultSchema,
  paymentIssuesResponseSchema,
  planCatalogSchema,
  planSchema,
  plansResponseSchema,
  subscriptionDetailSchema,
  subscriptionsResponseSchema,
  type AddressUpdateInput,
  type AdminDashboard,
  type AdminPlan,
  type AdminPlanInput,
  type AdminSettings,
  type AdminSettingsInput,
  type AdminCatalogProduct,
  type AdminOffer,
  type AdminOfferInput,
  type AdminOfferStateInput,
  type AdminProductCatalog,
  type AdminSubscription,
  type AdminSubscriptionSummary,
  type Branding,
  type BrandingSyncResult,
  type CheckoutView,
  type AuditEntry,
  type CancelInput,
  type CardUpdateInput,
  type CustomerSummary,
  type CreateIyzicoPricingPlanInput,
  type CreateIyzicoProductInput,
  type IyzicoCatalogPricingPlan,
  type IyzicoCatalogProduct,
  type LegalDocument,
  type NewSubscriptionInput,
  type OrderSyncIssue,
  type PaymentIssue,
  type PaymentResult,
  type Plan,
  type PlanCatalog,
  type SubscriptionDetail,
  type SubscriptionSummary,
} from "./schemas";
import { createMockPortalApi } from "./mock-api";

export interface ExchangeResult {
  customToken: string;
  redirectPath: string;
}

export interface CheckoutResult {
  subscriptionId: string;
  checkoutUrl: string;
}

export interface ActionResult {
  success: true;
  message: string;
}

export interface AdminSubscriptionFilters {
  status?: string;
  planId?: string;
  customer?: string;
  iyzicoReference?: string;
  from?: string;
  to?: string;
}

export interface PortalApi {
  exchangeCode(code: string, signal?: AbortSignal): Promise<ExchangeResult>;
  getCheckoutSession(token: string, signal?: AbortSignal): Promise<CheckoutView>;
  listPlans(signal?: AbortSignal): Promise<Plan[]>;
  getPlan(planId: string, signal?: AbortSignal): Promise<Plan>;
  getLegalDocument(type: LegalDocument["type"], signal?: AbortSignal): Promise<LegalDocument>;
  getPaymentResult(subscriptionId: string, signal?: AbortSignal): Promise<PaymentResult>;
  listSubscriptions(signal?: AbortSignal): Promise<SubscriptionSummary[]>;
  getSubscription(subscriptionId: string, signal?: AbortSignal): Promise<SubscriptionDetail>;
  initializeSubscription(input: NewSubscriptionInput): Promise<CheckoutResult>;
  updateAddress(subscriptionId: string, input: AddressUpdateInput): Promise<ActionResult>;
  initializeCardUpdate(subscriptionId: string, input: CardUpdateInput): Promise<CheckoutResult>;
  cancelSubscription(subscriptionId: string, input: CancelInput): Promise<ActionResult>;
  admin: {
    getDashboard(signal?: AbortSignal): Promise<AdminDashboard>;
    listPlans(signal?: AbortSignal): Promise<AdminPlan[]>;
    getPlanCatalog(signal?: AbortSignal): Promise<PlanCatalog>;
    upsertPlan(input: AdminPlanInput): Promise<Plan>;
    createIyzicoProduct(input: CreateIyzicoProductInput): Promise<IyzicoCatalogProduct>;
    createIyzicoPricingPlan(
      input: CreateIyzicoPricingPlanInput,
    ): Promise<IyzicoCatalogPricingPlan>;
    syncPlans(): Promise<ActionResult>;
    setPlanActive(planId: string, active: boolean): Promise<ActionResult>;
    listSubscriptions(
      filters?: AdminSubscriptionFilters,
      signal?: AbortSignal,
    ): Promise<AdminSubscriptionSummary[]>;
    getSubscription(subscriptionId: string, signal?: AbortSignal): Promise<AdminSubscription>;
    listPaymentIssues(signal?: AbortSignal): Promise<PaymentIssue[]>;
    retryPayment(cycleId: string): Promise<ActionResult>;
    resolveAmbiguousPaymentRetry(cycleId: string): Promise<ActionResult>;
    listOrderSyncIssues(signal?: AbortSignal): Promise<OrderSyncIssue[]>;
    retryOrderSync(cycleId: string): Promise<ActionResult>;
    markOrderSyncPermanent(cycleId: string): Promise<ActionResult>;
    listCustomers(signal?: AbortSignal): Promise<CustomerSummary[]>;
    listAudit(signal?: AbortSignal): Promise<AuditEntry[]>;
    getSettings(signal?: AbortSignal): Promise<AdminSettings>;
    updateSettings(input: AdminSettingsInput): Promise<AdminSettings>;
    getProductCatalog(
      params?: { cursor?: string; query?: string; pageSize?: number },
      signal?: AbortSignal,
    ): Promise<AdminProductCatalog>;
    upsertOffer(
      input: AdminOfferInput,
    ): Promise<{ offer: AdminOffer; repricedFrom?: string | undefined }>;
    repriceOffer(
      offerId: string,
      input: { expectedVersion: number; newSubscriptionPriceMinor: number },
    ): Promise<{
      offer: AdminOffer;
      repricedFrom?: string | undefined;
      providerPlanCreated?: string | undefined;
    }>;
    syncOfferShelfPrice(offerId: string): Promise<{ offer: AdminOffer }>;
    setOfferState(offerId: string, input: AdminOfferStateInput): Promise<AdminOffer>;
    setVariantSubscription(shopifyVariantGid: string, enabled: boolean): Promise<ActionResult>;
    setProductSubscription(
      shopifyProductGid: string,
      enabled: boolean,
      subscriptionOnly?: boolean,
    ): Promise<ActionResult>;
    getBranding(signal?: AbortSignal): Promise<Branding>;
    updateBranding(input: Branding): Promise<Branding>;
    syncBrandingFromTheme(): Promise<BrandingSyncResult>;
  };
}

export type { AdminCatalogProduct };

interface ApiRequestOptions extends Omit<RequestInit, "headers"> {
  /** true: zorunlu · false: hiç gönderme · "optional": varsa gönder (misafir ödemesi). */
  requiresAuth?: boolean | "optional";
  requiresAppCheck?: boolean;
}

function userFacingError(status: number): string {
  if (status === 401) return "Oturumunuz sona erdi. Lütfen Tropikal Akdeniz hesabınızla yeniden giriş yapın.";
  if (status === 403) return "Bu işlem için yetkiniz bulunmuyor.";
  if (status === 404) return "Aradığınız kayıt bulunamadı.";
  if (status === 409) return "Bu işlem zaten tamamlanmış veya şu anda işleniyor.";
  if (status === 429) return "Çok sık işlem yaptınız. Lütfen kısa bir süre sonra tekrar deneyin.";
  return "İşleminiz şu anda tamamlanamıyor. Lütfen biraz sonra tekrar deneyin.";
}

async function readError(response: Response): Promise<ApiError> {
  let code = `HTTP_${response.status}`;
  let correlationId: string | undefined;
  try {
    const payload: unknown = await response.json();
    if (typeof payload === "object" && payload !== null) {
      const root = payload as Record<string, unknown>;
      const nested =
        typeof root.error === "object" && root.error !== null
          ? (root.error as Record<string, unknown>)
          : root;
      if (typeof nested.code === "string") code = nested.code;
      if (typeof nested.correlationId === "string") correlationId = nested.correlationId;
    }
  } catch {
    // The response body is intentionally not surfaced to customer-facing UI.
  }
  return new ApiError(userFacingError(response.status), response.status, code, correlationId);
}

async function request<T>(
  path: string,
  schema: ZodType<T>,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { requiresAuth = true, requiresAppCheck = true, ...init } = options;
  const headers = new Headers({ Accept: "application/json" });
  if (init.body) headers.set("Content-Type", "application/json");
  headers.set("X-Correlation-ID", crypto.randomUUID());

  if (requiresAuth === true) {
    const user = getFirebaseAuth().currentUser;
    if (!user) throw new ApiError("Oturum açmanız gerekiyor.", 401, "AUTH_REQUIRED");
    headers.set("Authorization", `Bearer ${await user.getIdToken()}`);
  } else if (requiresAuth === "optional") {
    // Checkout works without an account. A customer who happens to be signed in is still
    // identified, which is what makes their saved addresses appear.
    const user = getFirebaseAuth().currentUser;
    if (user) headers.set("Authorization", `Bearer ${await user.getIdToken()}`);
  }

  if (requiresAppCheck) {
    headers.set("X-Firebase-AppCheck", await acquireAppCheckToken());
  }

  const response = await fetch(`${env.apiBaseUrl}${path}`, {
    ...init,
    headers,
    credentials: "omit",
  });
  if (!response.ok) throw await readError(response);

  const payload: unknown = response.status === 204 ? {} : await response.json();
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError(
      "Sunucudan beklenmeyen bir yanıt alındı. Lütfen tekrar deneyin.",
      502,
      "INVALID_API_RESPONSE",
    );
  }
  return parsed.data;
}

function jsonBody(input: unknown): Pick<RequestInit, "body" | "method"> {
  return { method: "POST", body: JSON.stringify(input) };
}

function queryString(filters: AdminSubscriptionFilters = {}): string {
  const search = new URLSearchParams();
  const entries: Array<[string, string | undefined]> = [
    ["status", filters.status],
    ["planId", filters.planId],
    ["customer", filters.customer],
    ["iyzicoReference", filters.iyzicoReference],
    ["from", filters.from],
    ["to", filters.to],
  ];
  entries.forEach(([key, value]) => {
    if (value) search.set(key, value);
  });
  const value = search.toString();
  return value ? `?${value}` : "";
}

const httpApi: PortalApi = {
  exchangeCode: (code, signal) =>
    request("/auth/exchange", exchangeResponseSchema, {
      ...jsonBody({ code }),
      ...(signal ? { signal } : {}),
      requiresAuth: false,
    }),
  getCheckoutSession: (token, signal) =>
    request(`/checkout/session/${encodeURIComponent(token)}`, checkoutViewSchema, {
      requiresAuth: "optional",
      ...(signal ? { signal } : {}),
    }),
  listPlans: async (signal) => {
    const response = await request("/plans", plansResponseSchema, {
      ...(signal ? { signal } : {}),
    });
    return response.items;
  },
  getPlan: (planId, signal) =>
    request(`/plans/${encodeURIComponent(planId)}`, planSchema, {
      ...(signal ? { signal } : {}),
    }),
  getLegalDocument: (type, signal) =>
    request(`/legal/${encodeURIComponent(type)}`, legalDocumentSchema, {
      ...(signal ? { signal } : {}),
      requiresAuth: false,
    }),
  getPaymentResult: (subscriptionId, signal) =>
    request(`/payments/result/${encodeURIComponent(subscriptionId)}`, paymentResultSchema, {
      ...(signal ? { signal } : {}),
    }),
  listSubscriptions: async (signal) => {
    const response = await request("/subscriptions", subscriptionsResponseSchema, {
      ...(signal ? { signal } : {}),
    });
    return response.items;
  },
  getSubscription: (subscriptionId, signal) =>
    request(`/subscriptions/${encodeURIComponent(subscriptionId)}`, subscriptionDetailSchema, {
      ...(signal ? { signal } : {}),
    }),
  initializeSubscription: (input) =>
    request("/subscriptions/initialize-checkout", checkoutResponseSchema, {
      ...jsonBody(input),
      requiresAuth: "optional",
    }),
  updateAddress: (subscriptionId, input) =>
    request(
      `/subscriptions/${encodeURIComponent(subscriptionId)}/address`,
      actionResponseSchema,
      { method: "PATCH", body: JSON.stringify(addressSchema.parse(input)) },
    ),
  initializeCardUpdate: (subscriptionId, input) =>
    request(
      `/subscriptions/${encodeURIComponent(subscriptionId)}/payment-method/initialize`,
      checkoutResponseSchema,
      jsonBody(input),
    ),
  cancelSubscription: (subscriptionId, input) =>
    request(
      `/subscriptions/${encodeURIComponent(subscriptionId)}/cancel`,
      actionResponseSchema,
      jsonBody(input),
    ),
  admin: {
    getDashboard: (signal) =>
      request("/admin/dashboard", adminDashboardSchema, { ...(signal ? { signal } : {}) }),
    listPlans: async (signal) => {
      const response = await request("/admin/plans", adminPlansResponseSchema, {
        ...(signal ? { signal } : {}),
      });
      return response.items;
    },
    getPlanCatalog: (signal) =>
      request("/admin/plan-catalog", planCatalogSchema, { ...(signal ? { signal } : {}) }),
    upsertPlan: (input) =>
      request(
        "/admin/plans",
        adminPlanSchema,
        jsonBody(adminPlanInputSchema.parse(input)),
      ),
    createIyzicoProduct: (input) =>
      request(
        "/admin/plan-catalog/iyzico-products",
        iyzicoCatalogProductSchema,
        jsonBody(createIyzicoProductInputSchema.parse(input)),
      ),
    createIyzicoPricingPlan: (input) =>
      request(
        `/admin/plan-catalog/iyzico-products/${encodeURIComponent(input.productReferenceCode)}/pricing-plans`,
        iyzicoCatalogPricingPlanSchema,
        jsonBody(createIyzicoPricingPlanInputSchema.omit({ productReferenceCode: true }).parse(input)),
      ),
    syncPlans: () => request("/admin/plans/sync", actionResponseSchema, jsonBody({})),
    setPlanActive: (planId, active) =>
      request(
        `/admin/plans/${encodeURIComponent(planId)}`,
        actionResponseSchema,
        { method: "PATCH", body: JSON.stringify({ active }) },
      ),
    listSubscriptions: async (filters, signal) => {
      const response = await request(
        `/admin/subscriptions${queryString(filters)}`,
        adminSubscriptionsResponseSchema,
        { ...(signal ? { signal } : {}) },
      );
      return response.items;
    },
    getSubscription: (subscriptionId, signal) =>
      request(
        `/admin/subscriptions/${encodeURIComponent(subscriptionId)}`,
        adminSubscriptionSchema,
        { ...(signal ? { signal } : {}) },
      ),
    listPaymentIssues: async (signal) => {
      const response = await request("/admin/payments", paymentIssuesResponseSchema, {
        ...(signal ? { signal } : {}),
      });
      return response.items;
    },
    retryPayment: (cycleId) =>
      request(
        `/admin/payments/${encodeURIComponent(cycleId)}/retry`,
        actionResponseSchema,
        jsonBody({ confirmation: true }),
      ),
    resolveAmbiguousPaymentRetry: (cycleId) =>
      request(
        `/admin/payments/${encodeURIComponent(cycleId)}/resolve-ambiguous`,
        actionResponseSchema,
        jsonBody({ confirmation: true, resolution: "CONFIRMED_NO_PROVIDER_PAYMENT" }),
      ),
    listOrderSyncIssues: async (signal) => {
      const response = await request("/admin/order-sync", orderSyncIssuesResponseSchema, {
        ...(signal ? { signal } : {}),
      });
      return response.items;
    },
    retryOrderSync: (cycleId) =>
      request(
        `/admin/order-sync/${encodeURIComponent(cycleId)}/retry`,
        actionResponseSchema,
        jsonBody({ confirmation: true }),
      ),
    markOrderSyncPermanent: (cycleId) =>
      request(
        `/admin/order-sync/${encodeURIComponent(cycleId)}/mark-permanent`,
        actionResponseSchema,
        jsonBody({ confirmation: true }),
      ),
    listCustomers: async (signal) => {
      const response = await request("/admin/customers", customersResponseSchema, {
        ...(signal ? { signal } : {}),
      });
      return response.items;
    },
    listAudit: async (signal) => {
      const response = await request("/admin/audit", auditResponseSchema, {
        ...(signal ? { signal } : {}),
      });
      return response.items;
    },
    getSettings: (signal) =>
      request("/admin/settings", adminSettingsSchema, { ...(signal ? { signal } : {}) }),
    updateSettings: (input) =>
      request("/admin/settings", adminSettingsSchema, {
        method: "PATCH",
        body: JSON.stringify(adminSettingsInputSchema.parse(input)),
      }),
    getProductCatalog: (params, signal) => {
      const search = new URLSearchParams();
      if (params?.cursor) search.set("cursor", params.cursor);
      if (params?.query) search.set("query", params.query);
      if (params?.pageSize) search.set("pageSize", String(params.pageSize));
      const suffix = search.toString();
      return request(
        `/admin/product-catalog${suffix ? `?${suffix}` : ""}`,
        adminProductCatalogSchema,
        { ...(signal ? { signal } : {}) },
      );
    },
    upsertOffer: (input) =>
      request(
        "/admin/offers",
        adminOfferUpsertResponseSchema,
        jsonBody(adminOfferInputSchema.parse(input)),
      ),
    repriceOffer: (offerId, input) =>
      request(
        `/admin/offers/${encodeURIComponent(offerId)}/price`,
        adminOfferRepriceResponseSchema,
        jsonBody(adminOfferRepriceInputSchema.parse(input)),
      ),
    syncOfferShelfPrice: (offerId) =>
      request(
        `/admin/offers/${encodeURIComponent(offerId)}/sync-shelf-price`,
        adminOfferSyncResponseSchema,
        { method: "POST" },
      ),
    setOfferState: (offerId, input) =>
      request(`/admin/offers/${encodeURIComponent(offerId)}`, adminOfferSchema, {
        method: "PATCH",
        body: JSON.stringify(adminOfferStateInputSchema.parse(input)),
      }),
    setVariantSubscription: (shopifyVariantGid, enabled) =>
      request(
        "/admin/variants/subscription",
        actionResponseSchema,
        jsonBody({ shopifyVariantGid, enabled }),
      ),
    setProductSubscription: (shopifyProductGid, enabled, subscriptionOnly) =>
      request(
        "/admin/products/subscription",
        actionResponseSchema,
        jsonBody({
          shopifyProductGid,
          enabled,
          ...(subscriptionOnly === undefined ? {} : { subscriptionOnly }),
        }),
      ),
    getBranding: (signal) =>
      request("/admin/branding", brandingSchema, { ...(signal ? { signal } : {}) }),
    updateBranding: (input) =>
      request("/admin/branding", brandingSchema, {
        method: "PUT",
        body: JSON.stringify(brandingSchema.parse(input)),
      }),
    syncBrandingFromTheme: () =>
      request("/admin/branding/sync-theme", brandingSyncResultSchema, jsonBody({})),
  },
};

export const api: PortalApi = env.useMockApi ? createMockPortalApi() : httpApi;

export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return "Beklenmeyen bir sorun oluştu. Lütfen tekrar deneyin.";
}
